/**
 * InquiryDrawer — 管理端线索详情侧边抽屉 v2
 * 功能：
 *   1. 展示客户完整咨询信息
 *   2. 多品明细报价表格（动态增删行）
 *   3. 实时汇总总价，保存 quoted_price 到 Supabase
 *   4. 一键生成含多行明细的 A4 PDF 报价单
 *
 * 样式：完全继承 admin-dark 深色主题 + 橙色主操作色
 */
import { useState, useRef, useEffect } from 'react';
import { SPEC_TAG_CFG } from '@/pages/admin/AdminRecipes';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Mail, Phone, Building2, Tag, MessageSquare, Calendar,
  Save, Download, Loader2, CheckCircle2, AlertCircle, FileText,
  Plus, Trash2,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

// ── 颜色常量 ─────────────────────────────────────────
const ORANGE = 'oklch(0.66 0.19 42)';
const ORANGE_HEX = '#E87722';

// ── 类型 ─────────────────────────────────────────────
interface QuoteLine {
  material: string;
  spec: string;
  qty: string;
  unit: string;
}

export interface InquiryItem {
  id: string;
  name: string;
  company: string | null;
  email: string;
  phone: string;
  material: string | null;
  message: string;
  status: string;
  quoted_price: number | null;
  quote_lines: QuoteLine[] | null;
  created_at: string;
  biz_no?: string | null;
}

interface Props {
  item: InquiryItem | null;
  onClose: () => void;
  onSaved: () => void;
}

// ── BOM 报价选项类型（从 materials_bom JOIN product_recipes 读取）────
interface BOMOption {
  material_code: string;
  material_name: string;
  finalPrice: number; // = min_price × discount + 模具费/kg
  // 直接存 product_recipes 的 spec 对象，实时引用，不再用字符串快照
  specs: Record<string, string | null> | null;
}

// ── 运费计算（与 AdminBOM 完全一致）────────────────────
function calcFreightPerKg(
  base: number, firstKg: number, extra: number, qty: number
): number {
  if (qty <= 0) return 0;
  const total = base + Math.max(0, qty - firstKg) * extra;
  return total / qty;
}

function calcFinalPrice(item: {
  min_price: number;
  discount: number | null;
  freight_cost: number | null;
  freight_base: number | null;
  freight_first_kg: number | null;
  freight_extra: number | null;
  order_qty_kg: number | null;
}): number {
  const discount = item.discount ?? 1;
  const qty = item.order_qty_kg ?? 100;
  const freightPerKg =
    item.freight_cost != null && item.freight_cost > 0
      ? item.freight_cost
      : calcFreightPerKg(
          item.freight_base ?? 20,
          item.freight_first_kg ?? 10,
          item.freight_extra ?? 1.5,
          qty
        );
  return item.min_price * 1.35 * discount + freightPerKg;
}

// ── 工具函数 ─────────────────────────────────────────
function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });
}
function nowDate() {
  return new Date().toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });
}
function genQuoteNo(id: string) {
  const d = new Date();
  return `PP-${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}-${id.slice(0,6).toUpperCase()}`;
}
function lineAmt(line: QuoteLine): number | null {
  const q = parseFloat(line.qty);
  const u = parseFloat(line.unit);
  return isNaN(q) || isNaN(u) ? null : q * u;
}
function fmt(n: number | null) {
  if (n == null) return '—';
  return `¥${n.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`;
}
function emptyLine(): QuoteLine {
  return { material: '', spec: '', qty: '', unit: '' };
}

// ── A4 HTML 报价单模板（完全照抄科慕代理商样例视觉设计）──────────
function buildQuotePdfHtml(data: {
  name: string;
  company: string;
  phone: string;
  email: string;
  material: string;
  message: string;
  lines: QuoteLine[];
  totalPrice: string;
  date: string;
  validUntil: string;
  quoteNo: string;
}) {
  const NAVY = '#002F6C';
  const NAVY_DARK = '#001F4E';
  const CUST_HDR_BG = '#3D4A5C';
  const STRIPE = '#E8F0F7';
  const BLACK = '#000000';
  const GRAY = '#555555';
  const LGRAY = '#888888';

  // 构建明细行 HTML（行号两位数，列名对照样例英中双语）
  const rowsHtml = data.lines
    .filter(l => l.material)
    .map((l, i) => {
      const amt = lineAmt(l);
      const bg = i % 2 === 1 ? `background:${STRIPE};` : '';
      return `
      <tr style="${bg}">
        <td style="border:1px solid #aaa;padding:4px 6px;text-align:center;white-space:nowrap;">${String(i + 1).padStart(2, '0')}</td>
        <td style="border:1px solid #aaa;padding:4px 6px;font-weight:600;">${l.material}</td>
        <td style="border:1px solid #aaa;padding:4px 6px;font-size:8.5px;color:#555;">${l.spec || '按需定制'}</td>
        <td style="border:1px solid #aaa;padding:4px 6px;text-align:right;white-space:nowrap;">${l.qty ? parseFloat(l.qty).toLocaleString('zh-CN') + ' kg' : '—'}</td>
        <td style="border:1px solid #aaa;padding:4px 6px;text-align:right;white-space:nowrap;">¥ ${l.unit ? parseFloat(l.unit).toLocaleString('zh-CN', { minimumFractionDigits: 2 }) + '/kg' : '—'}</td>
        <td style="border:1px solid #aaa;padding:4px 6px;text-align:right;font-weight:700;white-space:nowrap;min-width:120px;">${amt != null ? '¥ ' + amt.toLocaleString('zh-CN', { minimumFractionDigits: 2 }) : '—'}</td>
      </tr>`;
    }).join('');

  return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
<meta charset="UTF-8"/>
<style>
  @page { size: A4; margin: 0; }
  * { box-sizing: border-box; margin: 0; padding: 0; font-family: Arial, 'PingFang SC', 'Microsoft YaHei', sans-serif; }
  html, body { height: 100%; }
  body { background: #fff; color: ${BLACK}; font-size: 10.5px; line-height: 1.35; }
  /* ── 整页弹性布局：顶部固定 + 中间弹性 + 底部固定置底 ── */
  .page { display: flex; flex-direction: column; min-height: 100vh; }
  .page-top { flex-shrink: 0; }
  .page-middle { flex: 1; display: flex; flex-direction: column; justify-content: flex-start; padding: 10px 18mm 0; }
  .page-bottom { flex-shrink: 0; }
  /* 中间内容块保持自然间距，整体垂直居中，块间距=3×sec-title行高 */
  .middle-inner { display: flex; flex-direction: column; gap: 29px; }
  /* ── 顶部 banner + header ── */
  .top-banner { background: ${NAVY}; height: 12px; width: 100%; }
  .header-body { display: flex; justify-content: space-between; align-items: center; padding: 10px 18mm 8px 18mm; }
  .hdr-left { display: flex; flex-direction: column; justify-content: center; }
  /* 公司名与 PRICE QUOTATION 同字号 20px */
  .hdr-left .company-name { font-size: 20px; font-weight: 900; color: ${NAVY}; letter-spacing: 0.3px; margin-bottom: 3px; line-height: 1.1; }
  .hdr-left .company-en { font-size: 10px; color: ${LGRAY}; letter-spacing: 0.8px; text-transform: uppercase; margin-bottom: 5px; }
  .hdr-left .contact-line { font-size: 10.5px; color: ${GRAY}; line-height: 1.6; }
  .hdr-right { text-align: right; display: flex; flex-direction: column; align-items: flex-end; justify-content: center; }
  .hdr-right .doc-title-en { font-size: 20px; font-weight: 900; color: ${BLACK}; letter-spacing: 2px; text-transform: uppercase; line-height: 1.1; }
  .hdr-right .doc-title-cn { font-size: 12px; color: ${GRAY}; letter-spacing: 3px; margin-bottom: 5px; }
  .meta-tbl { font-size: 11px; color: ${GRAY}; border-collapse: collapse; margin-left: auto; }
  .meta-tbl td { padding: 1px 0 1px 12px; text-align: right; border: none; }
  .meta-tbl .ml { color: ${LGRAY}; text-align: left; padding-right: 6px; }
  .meta-tbl .mv { color: ${BLACK}; font-weight: 600; font-family: monospace; }
  .meta-tbl .mvhl { color: ${NAVY}; font-weight: 700; font-family: monospace; }
  /* ── 客户信息块 ── */
  .cust-wrap { border: 1px solid ${BLACK}; }
  .cust-grid { display: grid; grid-template-columns: 1fr 1fr; }
  .cust-col { border-right: 1px solid ${BLACK}; }
  .cust-col:last-child { border-right: none; }
  .cust-col-hdr { background: ${CUST_HDR_BG}; color: #fff; font-size: 9px; font-weight: 700; padding: 4px 8px; letter-spacing: 1px; text-transform: uppercase; border-bottom: 1px solid ${BLACK}; }
  .cust-body { padding: 5px 8px; font-size: 9px; line-height: 1.7; color: ${BLACK}; }
  .cust-row { display: flex; gap: 5px; }
  .cust-lbl { color: ${LGRAY}; min-width: 82px; flex-shrink: 0; }
  .cust-val { font-weight: 600; }
  /* ── 明细区 ── */
  .sec-title { font-size: 9.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: ${NAVY}; margin: 0 0 3px; }
  .detail-table { width: 100%; border-collapse: collapse; font-size: 9.5px; border: 1px solid #aaa; }
  .detail-table thead tr { background: ${NAVY}; color: #fff; }
  .detail-table thead th { padding: 5px 6px; font-size: 8.5px; font-weight: 700; letter-spacing: 0.5px; border: 1px solid ${NAVY_DARK}; text-align: left; }
  .detail-table tbody td { color: ${BLACK}; vertical-align: middle; }
  .subtotal-row td { border-top: 2px solid #555; background: #F5F5F5; padding: 4px 6px; font-size: 9.5px; }
  .total-row td { background: ${NAVY}; color: #fff; font-weight: 700; padding: 5px 6px; border: 1px solid ${NAVY_DARK}; }
  .total-label { font-size: 9.5px; letter-spacing: 1px; text-align: right; }
  .total-amt { font-size: 12px; font-family: monospace; font-weight: 900; text-align: right; white-space: nowrap; min-width: 130px; }
  /* ── 备注 & 条款 ── */
  .bottom-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 0; border: 1px solid #aaa; }
  .bc-col { padding: 5px 10px; }
  .bc-col:first-child { border-right: 1px solid #aaa; }
  .bc-col-title { font-size: 9px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: ${NAVY}; margin-bottom: 4px; padding-bottom: 3px; border-bottom: 1px solid #ddd; }
  .bc-ul { padding-left: 12px; }
  .bc-ul li { font-size: 8.5px; color: ${GRAY}; line-height: 1.6; }
  .bc-ol { padding-left: 14px; }
  .bc-ol li { font-size: 8.5px; color: ${GRAY}; line-height: 1.6; }
  /* ── 签署区 ── */
  .sign-wrap { display: grid; grid-template-columns: 1fr 1fr; border: 1px solid #aaa; }
  .sign-col { padding: 7px 12px 10px; min-height: 65px; }
  .sign-col:first-child { border-right: 1px solid #aaa; }
  .sign-col-title { font-size: 8.5px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: ${GRAY}; margin-bottom: 8px; }
  .sign-line { border-bottom: 1px solid ${BLACK}; height: 16px; margin-bottom: 2px; }
  .sign-line-lbl { font-size: 7.5px; color: ${LGRAY}; margin-bottom: 8px; }
  .sign-right-inner { display: flex; justify-content: space-between; align-items: flex-end; }
  .sign-lines-wrap { flex: 1; margin-right: 12px; }
  .stamp-circle { border: 2px dashed #aaa; border-radius: 50%; width: 58px; height: 58px; display: flex; align-items: center; justify-content: center; color: #bbb; font-size: 7.5px; text-align: center; line-height: 1.4; flex-shrink: 0; }
  /* ── 底部页脚 ── */
  .footer-bar { padding: 5px 18mm; border-top: 1px solid #ddd; display: flex; justify-content: space-between; font-size: 7.5px; color: ${LGRAY}; }
  .bot-banner { background: ${NAVY}; height: 12px; width: 100%; }
</style>
</head>
<body>
<div class="page">

  <!-- ① 顶部固定区：banner + header -->
  <div class="page-top">
    <div class="top-banner"></div>
    <div class="header-body">
      <div class="hdr-left">
        <div class="company-name">博元合成（北京）工业科技有限公司</div>
        <div class="company-en">BEIJING POLY-PRIME INDUSTRIAL TECHNOLOGY CO., LTD.</div>
        <div class="contact-line">
          地址：河北省沧州市橡胶产业基地<br/>
          电话：13426031477 &nbsp;&nbsp; 邮箱：info@poly-prime.com<br/>
          网址：www.poly-prime.com
        </div>
      </div>
      <div class="hdr-right">
        <div class="doc-title-en">PRICE QUOTATION</div>
        <div class="doc-title-cn">产品报价单</div>
        <table class="meta-tbl">
          <tr><td class="ml">Quotation No. 报价单号</td><td class="mvhl">${data.quoteNo}</td></tr>
          <tr><td class="ml">Date 日期</td><td class="mv">${data.date}</td></tr>
          <tr><td class="ml">Valid Until 有效期至</td><td class="mv">${data.validUntil}</td></tr>
          <tr><td class="ml">Page 页码</td><td class="mv">1 of 1</td></tr>
        </table>
      </div>
    </div>
  </div>

  <!-- ② 弹性中间区：BILL TO → 公司盖章，各分块间距均匀 -->
  <div class="page-middle">
    <div class="middle-inner">

      <div>
        <div class="sec-title">CUSTOMER INFORMATION / 买方信息</div>
        <div class="cust-wrap">
          <div class="cust-grid">
            <div class="cust-col">
              <div class="cust-col-hdr">BILL TO / 买方信息</div>
              <div class="cust-body">
                ${data.company ? `<div class="cust-row"><span class="cust-lbl">Company 公司</span><span class="cust-val">${data.company}</span></div>` : ''}
                ${data.name ? `<div class="cust-row"><span class="cust-lbl">Attn 联系人</span><span class="cust-val">${data.name}</span></div>` : ''}
                ${data.phone ? `<div class="cust-row"><span class="cust-lbl">Tel 电话</span><span class="cust-val">${data.phone}</span></div>` : ''}
                ${data.email ? `<div class="cust-row"><span class="cust-lbl">Email 邮箱</span><span class="cust-val">${data.email}</span></div>` : ''}
              </div>
            </div>
            <div class="cust-col">
              <div class="cust-col-hdr">SHIP TO / 收货信息 &amp; 交易条件</div>
              <div class="cust-body">
                ${data.company ? `<div class="cust-row"><span class="cust-lbl">Ship To 收货方</span><span class="cust-val">${data.company}</span></div>` : ''}
                <div class="cust-row"><span class="cust-lbl">Payment 付款方式</span><span class="cust-val">预付30%定金，余款发货前付清</span></div>
                <div class="cust-row"><span class="cust-lbl">Currency 币种</span><span class="cust-val">人民币 CNY (¥)</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div class="sec-title">QUOTATION DETAILS / 报价明细</div>
        <table class="detail-table">
          <thead>
            <tr>
              <th style="width:28px;text-align:center;">Line#</th>
              <th style="width:26%;">Product Grade / 产品名称</th>
              <th>Description / 产品描述 &amp; 规格</th>
              <th style="text-align:right;width:68px;">Qty / 数量</th>
              <th style="text-align:right;width:90px;">Unit Price / 单价 (¥/kg)</th>
              <th style="text-align:right;width:90px;">Amount / 金额 (¥)</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
          <tfoot>
            <tr class="subtotal-row">
              <td colspan="5" style="text-align:right;font-weight:700;">SUBTOTAL &nbsp; 小计</td>
              <td style="text-align:right;font-family:monospace;font-weight:700;white-space:nowrap;min-width:130px;">¥ ${data.totalPrice}</td>
            </tr>
            <tr class="total-row">
              <td colspan="5" class="total-label">TOTAL AMOUNT &nbsp; 合计总价 &nbsp; (人民币含税)</td>
              <td class="total-amt">¥ ${data.totalPrice}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div class="bottom-cols">
        <div class="bc-col">
          <div class="bc-col-title">PRODUCT NOTES / 产品备注</div>
          <ul class="bc-ul">
            <li>每批次产品均附出厂检验报告（CoA），可按需提供 SDS 安全技术说明书。</li>
            <li>产品符合 GB/T 相关国家标准；如有特殊技术要求，请在订单中书面注明。</li>
            <li>保质期自生产之日起 12 个月，请于阴凉干燥处密封存储（温度 10–30°C，避免阳光直射）。</li>
            <li>产品按非危险品运输；运费由买方承担，另行计算，不含于本报价。</li>
            <li>订单确认后交期约 5–7 个工作日（受库存影响，以最终确认为准）。</li>
            <li>可提供定制包装，最低订量要求请另议；样品测试需另行确认打样周期与费用。</li>
            ${data.message ? '<li>' + data.message.replace(/\n/g, '</li><li>') + '</li>' : ''}
          </ul>
        </div>
        <div class="bc-col">
          <div class="bc-col-title">TERMS &amp; CONDITIONS / 条款</div>
          <ol class="bc-ol">
            <li><strong>Validity 有效期：</strong>本报价自发出之日起 30 天内有效，逾期请重新询价。</li>
            <li><strong>Payment 付款：</strong>预付 30% 定金，余款发货前付清；大额订单可协商分期付款安排。</li>
            <li><strong>Delivery 交货：</strong>订单确认后 5–7 个工作日发货；运费另计，由买方承担。</li>
            <li><strong>Quality 质量：</strong>产品符合国家标准及双方确认技术协议；质量异议须在到货 7 日内书面提出，逾期视为验收合格。</li>
            <li><strong>Force Majeure 不可抗力：</strong>因自然灾害、政策法规调整等不可抗力事件导致的延误，卖方不承担违约责任，双方协商解决。</li>
            <li><strong>Governing Law 法律适用：</strong>本报价及后续合同均受中华人民共和国法律管辖，争议提交卖方所在地有管辖权法院处理。</li>
          </ol>
        </div>
      </div>

      <div class="sign-wrap">
        <div class="sign-col">
          <div class="sign-col-title">Prepared by / 制单人</div>
          <div class="sign-line"></div>
          <div class="sign-line-lbl">Signature &amp; Name / Title &nbsp; 签名 · 姓名 · 职位</div>
          <div class="sign-line"></div>
          <div class="sign-line-lbl">Date / 日期</div>
        </div>
        <div class="sign-col">
          <div class="sign-col-title">Authorized by / 法人 · 负责人授权</div>
          <div class="sign-right-inner">
            <div class="sign-lines-wrap">
              <div class="sign-line"></div>
              <div class="sign-line-lbl">Authorized Signature &amp; Name / Title &nbsp; 授权签名 · 姓名 · 职位</div>
              <div class="sign-line"></div>
              <div class="sign-line-lbl">Date / 日期</div>
            </div>
            <div class="stamp-circle">COMPANY<br/>SEAL<br/>公司盖章</div>
          </div>
        </div>
      </div>

    </div>
  </div>

  <!-- ③ 底部固定区：页脚 + 蓝色条，始终置底 -->
  <div class="page-bottom">
    <div class="footer-bar">
      <span>博元合成（北京）工业科技有限公司 &nbsp;|&nbsp; 河北省沧州市橡胶产业基地 &nbsp;|&nbsp; Tel: 13426031477 &nbsp;|&nbsp; Email: info@poly-prime.com &nbsp;|&nbsp; www.poly-prime.com</span>
      <span>1/1</span>
    </div>
    <div class="bot-banner"></div>
  </div>

</div>

</body>
</html>`;
}

// ── 主组件 ────────────────────────────────────────────
export function InquiryDrawer({ item, onClose, onSaved }: Props) {
  // 多品明细 —— 初始值（后续由 useEffect 覆盖为数据库最新值）
  const [lines, setLines] = useState<QuoteLine[]>(() => [
    { material: item?.material ?? '', spec: '', qty: '1', unit: item?.quoted_price ? String(item.quoted_price) : '' }
  ]);
  const [saving, setSaving]     = useState(false);
  const [exporting, setExporting] = useState(false);
  const [saveOk, setSaveOk]     = useState(false);
  const [saveErr, setSaveErr]   = useState('');
  const printFrameRef = useRef<HTMLIFrameElement | null>(null);
  // 控制每行材料下拉的展开/收起（null=全部收起，数字=对应行展开）
  const [openDropdownIdx, setOpenDropdownIdx] = useState<number | null>(null);

  // ── BOM 报价选项（从 materials_bom 表动态读取，组件挂载时加载一次）──
  const [bomOptions, setBomOptions] = useState<BOMOption[]>([]);
  const [bomLoading, setBomLoading] = useState(true);

useEffect(() => {
    supabase
      .from('materials_bom')
      .select(`
        material_code, material_name, min_price, discount,
        freight_cost, freight_base, freight_first_kg, freight_extra, order_qty_kg,
        recipe_id,
        product_recipes (
          spec_temp_range, spec_acid_resistance, spec_alkali_resistance,
          spec_oil_resistance, spec_ozone_resistance, spec_compression_set,
          spec_tensile_strength, spec_elongation, spec_seawater_resistance,
          spec_compliance, spec_qa_req, spec_tolerance,
          spec_thickness, spec_hardness
        )
      `)
      .order('material_code')
      .then(({ data }) => {
        if (data) {
          setBomOptions(
            (data as unknown as Array<{
              material_code: string;
              material_name: string;
              min_price: number;
              discount: number | null;
              freight_cost: number | null;
              freight_base: number | null;
              freight_first_kg: number | null;
              freight_extra: number | null;
              order_qty_kg: number | null;
              recipe_id: string | null;
              product_recipes: Record<string, string | null> | null;
            }>).map(row => ({
              material_code: row.material_code,
              material_name: row.material_name,
              finalPrice: calcFinalPrice(row),
              specs: row.product_recipes ?? null, // 实时引用，不再用 notes 字符串
            }))
          );
        }
        setBomLoading(false);
      });
  }, []);

  // ── 关键修复：每次打开不同线索时，从数据库读取完整 quote_lines 明细数组 ──
  // 包含所有行的材料、规格、数量、单价，原封不动还原用户上次填写的内容
  useEffect(() => {
    if (!item?.id) return;
    setSaveOk(false);
    setSaveErr('');
    supabase
      .from('inquiries')
      .select('quoted_price, material, quote_lines')
      .eq('id', item.id)
      .single()
      .then(({ data }) => {
        // 优先还原完整明细数组
        if (data?.quote_lines && Array.isArray(data.quote_lines) && (data.quote_lines as QuoteLine[]).length > 0) {
          setLines(data.quote_lines as QuoteLine[]);
        } else {
          // 降级：仅有 quoted_price，构造一行
          const freshPrice = data?.quoted_price != null ? String(data.quoted_price) : '';
          const freshMaterial = data?.material ?? item.material ?? '';
          setLines([{ material: freshMaterial, spec: '', qty: freshPrice ? '1' : '', unit: freshPrice }]);
        }
      });
  }, [item?.id]); // 仅在切换不同线索时触发，不影响用户在抽屉内的实时编辑

  const IS = {
    background: '#FFFFFF',
    border: '1px solid #D0D5E2',
    color: '#1A2337',
    borderRadius: '4px',
    padding: '6px 9px',
    fontSize: '12px',
    outline: 'none',
    width: '100%',
  } as React.CSSProperties;

  // 汇总总价
  const grandTotal = lines.reduce((s, l) => s + (lineAmt(l) ?? 0), 0);

  // 行操作
  const updateLine = (idx: number, key: keyof QuoteLine, val: string) =>
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, [key]: val } : l));
  // 批量更新单行多个字段（选中材料后同步填入单价使用）
  const updateLineBatch = (idx: number, patch: Partial<QuoteLine>) =>
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const addLine = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (idx: number) => setLines(prev => prev.filter((_, i) => i !== idx));

  // 保存报价到 inquiries.quoted_price
  const handleSavePrice = async () => {
    if (!item) return;
    if (grandTotal <= 0) { setSaveErr('请至少填写一行有效的材料、数量和单价'); return; }
    setSaving(true); setSaveErr('');
    const { error } = await supabase
      .from('inquiries')
      .update({
        quoted_price: grandTotal,
        status: 'quoted',
        quote_lines: lines,   // 同时保存完整明细数组，下次打开时原封不动还原
      })
      .eq('id', item.id);
    setSaving(false);
    if (error) { setSaveErr('保存失败：' + error.message); }
    else { setSaveOk(true); onSaved(); setTimeout(() => setSaveOk(false), 2500); }
  };

  // 导出 PDF
  const handleExportPDF = () => {
    if (!item) return;
    const priceStr = grandTotal > 0
      ? grandTotal.toLocaleString('zh-CN', { minimumFractionDigits: 2 })
      : '待确认';
    setExporting(true);

    // 计算有效期（30天后）
    const validUntilDate = (() => {
      const d = new Date();
      d.setDate(d.getDate() + 30);
      return d.toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });
    })();

    const html = buildQuotePdfHtml({
      name:       item.name,
      company:    item.company ?? '',
      phone:      item.phone,
      email:      item.email,
      material:   item.material ?? '',
      message:    item.message,
      lines,
      totalPrice: priceStr,
      date:       nowDate(),
      validUntil: validUntilDate,
      quoteNo:    item.biz_no ?? genQuoteNo(item.id),
    });

    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;inset:0;width:0;height:0;border:none;opacity:0;pointer-events:none;';
    document.body.appendChild(iframe);
    printFrameRef.current = iframe;

    const doc = iframe.contentWindow?.document;
    if (doc) {
      doc.open(); doc.write(html); doc.close();
      iframe.contentWindow?.addEventListener('load', () => {
        setTimeout(() => {
          iframe.contentWindow?.print();
          setExporting(false);
          setTimeout(() => { document.body.removeChild(iframe); }, 2000);
        }, 400);
      });
    } else {
      setExporting(false);
    }
  };

  if (!item) return null;

  return (
    <AnimatePresence>
      {item && (
        <>
          {/* 遮罩 */}
          <motion.div
            key="drawer-mask"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/70"
          />

          {/* 抽屉面板 */}
          <motion.aside
            key="drawer-panel"
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 340, damping: 36 }}
            className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-xl flex flex-col"
            style={{ background: '#FFFFFF', borderLeft: '1px solid #E5E7EB' }}
          >
            {/* 顶栏 */}
            <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
              style={{ borderBottom: '1px solid #E5E7EB' }}>
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" style={{ color: ORANGE }} />
                <span className="font-bold text-sm" style={{ color: '#1A2337' }}>线索详情 & 多品报价</span>
              </div>
              <button onClick={onClose} className="p-1.5 rounded transition-colors hover:bg-black/5" style={{ color: '#5A6780' }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* 滚动内容 */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

              {/* 客户信息 */}
              <section>
                <h3 className="text-[10px] font-bold tracking-[0.25em] uppercase mb-3" style={{ color: ORANGE }}>客户信息</h3>
                <div className="space-y-2">

                  {/* 线索编号 — 独占一行，橙色高亮 */}
                  {item.biz_no && (
                    <div className="flex items-center gap-2 px-2 py-1.5 rounded-sm" style={{ background: 'rgba(232,119,34,0.07)', border: '1px solid rgba(232,119,34,0.2)' }}>
                      <Tag className="w-3 h-3 flex-shrink-0" style={{ color: ORANGE }} />
                      <span className="text-[10px] uppercase tracking-widest flex-shrink-0" style={{ color: '#9CA3AF' }}>线索编号</span>
                      <span className="text-xs font-mono font-bold tracking-wider" style={{ color: ORANGE }}>{item.biz_no}</span>
                    </div>
                  )}

                  {/* 联系人 + 电话 — 两列并排 */}
                  <div className="grid grid-cols-2 gap-x-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest mb-0.5" style={{ color: '#9CA3AF' }}>联系人</div>
                      <span className="text-sm font-medium" style={{ color: '#1A2337' }}>{item.name}</span>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest mb-0.5" style={{ color: '#9CA3AF' }}>电话</div>
                      <a href={`tel:${item.phone}`} className="text-sm font-medium font-mono hover:underline" style={{ color: ORANGE }}>{item.phone}</a>
                    </div>
                  </div>

                  {/* 公司 — 独占一行 */}
                  <div>
                    <div className="text-[10px] uppercase tracking-widest mb-0.5" style={{ color: '#9CA3AF' }}>公司</div>
                    <div className="flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#9CA3AF' }} />
                      <span className="text-sm font-medium" style={{ color: '#1A2337' }}>{item.company ?? '—'}</span>
                    </div>
                  </div>

                  {/* 邮箱 — 独占一行 */}
                  <div>
                    <div className="text-[10px] uppercase tracking-widest mb-0.5" style={{ color: '#9CA3AF' }}>邮箱</div>
                    <div className="flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#9CA3AF' }} />
                      <a href={`mailto:${item.email}`} className="text-sm font-medium font-mono hover:underline break-all" style={{ color: ORANGE }}>{item.email}</a>
                    </div>
                  </div>

                  {/* 咨询材料 + 提交时间 — 两列并排 */}
                  <div className="grid grid-cols-2 gap-x-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest mb-0.5" style={{ color: '#9CA3AF' }}>咨询材料</div>
                      <span className="text-sm font-medium" style={{ color: '#1A2337' }}>{item.material ?? '未选择'}</span>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest mb-0.5" style={{ color: '#9CA3AF' }}>提交时间</div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 flex-shrink-0" style={{ color: '#9CA3AF' }} />
                        <span className="text-sm" style={{ color: '#1A2337' }}>{fmtDate(item.created_at)}</span>
                      </div>
                    </div>
                  </div>

                </div>
              </section>

              {/* 咨询内容 */}
              <section>
                <h3 className="text-[10px] font-bold tracking-[0.25em] uppercase mb-3" style={{ color: ORANGE }}>咨询内容</h3>
                <div className="flex items-start gap-3">
                  <MessageSquare className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#9CA3AF' }} />
                  <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: '#374151' }}>{item.message}</p>
                </div>
              </section>

              <hr style={{ borderColor: '#E5E7EB' }} />

              {/* ── 多品报价明细 ── */}
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[10px] font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>
                    报价明细（多品支持）
                  </h3>
                  <button onClick={addLine}
                    className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-sm border border-border hover:border-orange-400/40 transition-colors"
                    style={{ color: '#8A9BB5' }}
                  >
                    <Plus className="w-3 h-3" />添加材质
                  </button>
                </div>

                {/* 列头 */}
                <div className="grid gap-1 text-[9px] font-mono font-bold tracking-widest uppercase mb-1.5 px-0.5"
                  style={{ color: '#3A4A5C', gridTemplateColumns: '2fr 2fr 1fr 1fr 28px' }}>
                  <span>材料</span><span>规格</span><span>数量(kg)</span><span>单价(元/kg)</span><span></span>
                </div>

                <div className="space-y-2">
                  {lines.map((line, idx) => {
                    const lt = lineAmt(line);
                    return (
                      <div key={idx}>
                        <div className="grid gap-1 items-center" style={{ gridTemplateColumns: '2fr 2fr 1fr 1fr 28px' }}>
                          {/* 材料 —— 自定义下拉，显示名称+价格+小标签 */}
                          <div className="relative">
                            {/* 触发按钮 */}
                            <button
                              type="button"
                              onClick={() => setOpenDropdownIdx(openDropdownIdx === idx ? null : idx)}
                              disabled={bomLoading}
                              style={{ ...IS, textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                            >
                              <span style={{ color: line.material ? '#1A2337' : '#9CA3AF', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                {bomLoading ? '加载中…' : (line.material || '— 选择产品 —')}
                              </span>
                              <span style={{ color: '#9CA3AF', fontSize: '10px', marginLeft: '4px' }}>▾</span>
                            </button>

                            {/* 下拉列表 */}
                            {openDropdownIdx === idx && (
                              <div
                                className="absolute z-50 left-0 mt-1 w-[320px] max-h-64 overflow-y-auto rounded shadow-lg"
                                style={{ background: '#FFFFFF', border: '1px solid #D0D5E2' }}
                              >
                                {/* 空选项 */}
                                <div
                                  className="px-3 py-2 cursor-pointer hover:bg-gray-50 text-xs"
                                  style={{ color: '#9CA3AF', borderBottom: '1px solid #F3F4F6' }}
                                  onClick={() => { updateLine(idx, 'material', ''); setOpenDropdownIdx(null); }}
                                >
                                  — 选择产品 —
                                </div>

                                {/* BOM 选项列表：标签直接从 product_recipes JOIN 字段读取，与配方库实时同步 */}
                                {bomOptions.map(o => {
                                  // 直接从 specs 对象用 SPEC_TAG_CFG 渲染，无需解析字符串
                                  const tags: { key: string; label: string; value: string }[] = [];
                                  if (o.specs) {
                                    // 密度和硬度单独处理
                                    if (o.specs.spec_thickness) {
                                      tags.push({ key: 'spec_thickness', label: '密度', value: o.specs.spec_thickness });
                                    }
                                    if (o.specs.spec_hardness) {
                                      tags.push({ key: 'spec_hardness', label: '硬度', value: o.specs.spec_hardness });
                                    }
                                    // 其余技术标签从 SPEC_TAG_CFG 读取
                                    Object.entries(SPEC_TAG_CFG).forEach(([key, cfg]) => {
                                      const val = o.specs![key];
                                      if (val && val !== '—') {
                                        tags.push({ key, label: cfg.label, value: val });
                                      }
                                    });
                                  }
                                  return (
                                    <div
                                      key={o.material_code}
                                      className="px-3 py-2 cursor-pointer hover:bg-orange-50 transition-colors"
                                      style={{ borderBottom: '1px solid #F3F4F6', background: line.material === o.material_name ? 'rgba(232,119,34,0.06)' : undefined }}
                                      onClick={() => {
                                        updateLineBatch(idx, { material: o.material_name, unit: o.finalPrice.toFixed(2) });
                                        setOpenDropdownIdx(null);
                                      }}
                                    >
                                      {/* 第一行：名称 + 编号 + 建议价 */}
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs font-medium" style={{ color: '#1A2337' }}>
                                          {o.material_name}
                                          <span className="ml-1 font-normal" style={{ color: '#9CA3AF' }}>({o.material_code})</span>
                                        </span>
                                        <span className="text-xs font-mono font-bold ml-2 flex-shrink-0" style={{ color: '#E87722' }}>
                                          ¥{o.finalPrice.toFixed(2)}/kg
                                        </span>
                                      </div>
                                      {/* 第二行：小标签，与配方库完全一致 */}
                                      {tags.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {tags.map(t => {
                                            // 密度和硬度用固定颜色
                                            if (t.key === 'spec_thickness') {
                                              return (
                                                <span key={t.key} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium"
                                                  style={{ background: 'rgba(180,83,9,0.10)', color: '#B45309', border: '1px solid #B4530955' }}>
                                                  {t.label}: {t.value}
                                                </span>
                                              );
                                            }
                                            if (t.key === 'spec_hardness') {
                                              return (
                                                <span key={t.key} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium"
                                                  style={{ background: 'rgba(29,78,216,0.09)', color: '#1D4ED8', border: '1px solid #1D4ED855' }}>
                                                  {t.label}: {t.value}
                                                </span>
                                              );
                                            }
                                            const cfg = SPEC_TAG_CFG[t.key];
                                            if (!cfg) return null;
                                            return (
                                              <span key={t.key} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium"
                                                style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}30` }}>
                                                {cfg.label}: {t.value}
                                              </span>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}

                                {/* 若当前行已有值但不在 BOM 列表中，保留显示 */}
                                {line.material && !bomOptions.find(o => o.material_name === line.material) && (
                                  <div
                                    className="px-3 py-2 cursor-pointer hover:bg-gray-50 text-xs"
                                    style={{ color: '#1A2337', background: 'rgba(232,119,34,0.06)' }}
                                    onClick={() => setOpenDropdownIdx(null)}
                                  >
                                    {line.material}（自定义）
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          {/* 规格 */}
                          <input value={line.spec} onChange={e => updateLine(idx, 'spec', e.target.value)}
                            style={IS} placeholder="厚度/宽度等" />
                          {/* 数量 */}
                          <input type="number" value={line.qty} onChange={e => updateLine(idx, 'qty', e.target.value)}
                            style={IS} placeholder="0" min="0" />
                          {/* 单价 —— 选中产品后自动预填，仍可手动覆盖 */}
                          <input type="number" value={line.unit} onChange={e => updateLine(idx, 'unit', e.target.value)}
                            style={IS} placeholder="0.00" min="0" step="0.01" />
                          {/* 删除 */}
                          <button onClick={() => removeLine(idx)} disabled={lines.length === 1}
                            className="p-1 rounded-sm border border-border hover:border-red-400/40 disabled:opacity-20 transition-colors flex items-center justify-center"
                          >
                            <Trash2 className="w-3 h-3 text-red-400" />
                          </button>
                        </div>
                        {/* 单价来源提示 + 行小计 */}
                        <div className="flex items-center justify-between mt-0.5 pr-8">
                          {/* 若单价与 BOM 最终报价匹配，显示来源标记 */}
                          {(() => {
                            const matched = bomOptions.find(o => o.material_name === line.material);
                            if (matched && line.unit && Math.abs(parseFloat(line.unit) - matched.finalPrice) < 0.01) {
                              return (
                                <span className="text-[10px] font-mono" style={{ color: '#4ADE80' }}>
                                  ✓ 报价同步自商务测算
                                </span>
                              );
                            }
                            if (line.unit && line.material) {
                              return (
                                <span className="text-[10px]" style={{ color: '#7A8EA8' }}>
                                  已手动调整单价
                                </span>
                              );
                            }
                            return <span />;
                          })()}
                          {lt !== null && (
                            <div className="text-[11px] font-mono" style={{ color: ORANGE }}>
                              小计 {fmt(lt)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* 合计总价 */}
                {grandTotal > 0 && (
                  <div className="mt-4 rounded-sm px-4 py-3 text-right border border-orange-400/20"
                    style={{ background: `${ORANGE}15` }}>
                    <span className="text-xs font-mono mr-3" style={{ color: '#8A9BB5' }}>合计总价</span>
                    <span className="text-lg font-black font-mono" style={{ color: ORANGE }}>
                      {fmt(grandTotal)}
                    </span>
                  </div>
                )}
              </section>

              <hr style={{ borderColor: '#E5E7EB' }} />

              {/* 保存报价 */}
              <section className="space-y-3">
                <h3 className="text-[10px] font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>
                  保存报价至线索记录
                </h3>
                <p className="text-xs" style={{ color: '#7A8EA8' }}>
                  点击下方按钮将上方合计总价（{fmt(grandTotal > 0 ? grandTotal : null)}）写入数据库，并将线索状态更新为"已报价"。
                </p>
                {saveErr && (
                  <div className="flex items-center gap-2 text-xs" style={{ color: '#F87171' }}>
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />{saveErr}
                  </div>
                )}
                {saveOk && (
                  <div className="flex items-center gap-2 text-xs" style={{ color: '#4ADE80' }}>
                    <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />报价已保存，线索状态已更新为"已报价"
                  </div>
                )}
                <button
                  onClick={handleSavePrice}
                  disabled={saving || grandTotal <= 0}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded font-bold text-sm transition-opacity disabled:opacity-50"
                  style={{ background: ORANGE, color: '#FFFFFF' }}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  保存报价 & 更新状态
                </button>
              </section>
            </div>

            {/* 底部 PDF 导出 */}
            <div className="px-6 py-4 flex-shrink-0" style={{ borderTop: '1px solid #E5E7EB' }}>
              <button
                onClick={handleExportPDF}
                disabled={exporting}
                className="w-full flex items-center justify-center gap-2 py-3 rounded font-bold text-sm transition-all disabled:opacity-50 hover:brightness-110"
                style={{ background: ORANGE, color: '#FFFFFF', letterSpacing: '0.05em' }}
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {exporting ? '正在生成报价单…' : '生成并下载报价单 PDF'}
              </button>
              <p className="text-center text-[10px] mt-2" style={{ color: '#7A8EA8' }}>
                PDF 包含完整多品明细表 · 浏览器打印对话框 → 另存为 PDF
              </p>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}